import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getQuotation } from "../../core/api/quotationService";
import { getPatient } from "../../core/api/patientService";
import { CLINIC_INFO } from "../../core/constants/clinicInfo";
import { calculateAge } from "../../core/utils/calculateAge";

const fmt = (v) => Number(v || 0).toLocaleString("en-IN");
const formatDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
};

const QuotationPrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getQuotation(id)
      .then(async (res) => {
        if (cancelled) return;
        const q = res.data;
        setQuotation(q);
        const patientId = q.patientID ?? q.patientId;
        const patientRes = patientId ? await getPatient(patientId).catch(() => null) : null;
        if (cancelled) return;
        if (patientRes) setPatient(patientRes.data);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  if (!quotation) return <div className="page-wrapper"><div className="content"><p>Quotation not found.</p></div></div>;

  const rows = (quotation.lines || []).map((l) => ({
    name: l.procedureName || l.name || "Treatment",
    qty: l.qty ?? l.quantity ?? 1,
    rate: l.rate,
    amount: l.amount,
  }));

  const displayAge = calculateAge(patient?.dob) ?? patient?.age ?? quotation.patientAge ?? "—";
  const displayGender = patient?.gender || quotation.patientGender || "—";
  const displayName = patient?.patientName || quotation.patientName || "—";
  const displayMobile = patient?.mobileNumber || quotation.mobileNumber || quotation.patientMobile || "—";

  return (
    <div className="page-wrapper invoice-print-page">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .header, #sidebar, .page-header, .no-print { display: none !important; }
          .page-wrapper { margin: 0 !important; padding: 0 !important; }
          .invoice-print-area { box-shadow: none !important; min-height: 100vh; }
          body { background: #fff !important; }
        }
        .invoice-print-area { max-width: 820px; margin: 0 auto; background: #fff; padding: 40px; font-family: Arial, sans-serif; color: #000; display: flex; flex-direction: column; }
        .invoice-print-area table { width: 100%; border-collapse: collapse; }
        .invoice-print-area th, .invoice-print-area td { border: 1px solid #000; padding: 8px 10px; }
        .invoice-print-area .patient-box { border: 1px solid #000; padding: 12px 16px; margin-bottom: 0; }
        .invoice-print-area .patient-box-cols { display: flex; justify-content: space-between; gap: 20px; }
        .invoice-print-area .patient-box-cols > div > div { margin-bottom: 8px; }
        .invoice-print-area .patient-box-cols > div > div:last-child { margin-bottom: 0; }
        .invoice-print-area .patient-box-cols .patient-box-right { text-align: right; }
        .invoice-print-area .section-title { text-align: center; font-weight: bold; letter-spacing: 1px; margin: 20px 0; text-decoration: underline; }
        .invoice-print-area .summary-table { width: 260px; margin-left: auto; margin-top: -1px; }
        .invoice-print-area .summary-table td:first-child { font-weight: 600; }
        .invoice-print-area .summary-table td:last-child { text-align: right; }
        .invoice-print-area .summary-table .grand-total td { font-weight: bold; font-size: 1.05em; }
        .invoice-print-area .print-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; }
        .invoice-print-area .print-logo { max-height: 70px; }
        .invoice-print-area .doctor-block { text-align: right; }
        .invoice-print-area .doctor-block .doctor-entry { margin-bottom: 8px; }
        .invoice-print-area .doctor-block .doctor-entry:last-child { margin-bottom: 0; }
        .invoice-print-area .doctor-block .doctor-name { font-weight: bold; font-size: 1.02em; }
        .invoice-print-area .doctor-block .doctor-qual { font-size: 0.82em; line-height: 1.35; color: #222; }
        .invoice-print-area .print-rule { border: none; border-top: 2px solid #000; margin: 0 0 24px; }
        .invoice-print-area .print-footer { margin-top: auto; padding-top: 40px; font-size: 0.82em; color: #333; }
        .invoice-print-area .print-footer-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
        .invoice-print-area .print-footer-dots span { display: inline-block; width: 6px; height: 6px; border-radius: 50%; margin-right: 4px; }
        .invoice-print-area .print-footer-strapline { font-style: italic; color: #555; }
        .invoice-print-area .print-footer-rule { border: none; border-top: 1px solid #999; margin: 0 0 8px; }
        .invoice-print-area .print-footer-bottom { display: flex; justify-content: space-between; align-items: flex-end; }
        .invoice-print-area .print-footer-bottom .print-footer-right { text-align: right; }
        .invoice-print-area .print-footer .clinic-name-footer { font-weight: bold; font-size: 1.05em; color: #000; margin-bottom: 2px; }
      `}</style>

      <div className="content no-print d-flex justify-content-end gap-2 mb-3">
        <button
          className="btn btn-cancel"
          onClick={() => {
            if (window.opener) window.close();
            else navigate("/quotations");
          }}
        >
          Back
        </button>
        <button className="btn btn-submit" onClick={() => window.print()}>Print</button>
      </div>

      <div className="invoice-print-area">
        <div className="print-header">
          <img src={CLINIC_INFO.logo} alt={CLINIC_INFO.clinicName} className="print-logo" />
          <div className="doctor-block">
            {CLINIC_INFO.doctors.map((doc, idx) => (
              <div className="doctor-entry" key={idx}>
                <div className="doctor-name">{doc.name}</div>
                {doc.qualifications.map((q, qIdx) => (
                  <div className="doctor-qual" key={qIdx}>{q}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <hr className="print-rule" />

        <div className="patient-box">
          <div className="patient-box-cols">
            <div className="patient-box-left">
              <div>PATIENT NAME : {displayName}</div>
              <div>AGE/GENDER : {displayAge}/{displayGender}</div>
              <div>CONTACT NUMBER : {displayMobile}</div>
            </div>
            <div className="patient-box-right">
              <div>DATE : {formatDate(quotation.quotationDate)}</div>
              <div>QUOTATION NO : {quotation.quotationNo || quotation.id}</div>
            </div>
          </div>
        </div>

        <div className="section-title">QUOTATION</div>

        <table>
          <thead>
            <tr>
              <th style={{ width: 60 }}>SL.NO</th>
              <th>TREATMENT</th>
              <th style={{ width: 100 }}>QUANTITY</th>
              <th style={{ width: 110 }}>RATE</th>
              <th style={{ width: 130 }}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: "center" }}>No treatments</td></tr>
            ) : rows.map((r, idx) => (
              <tr key={idx}>
                <td style={{ textAlign: "center" }}>{idx + 1}</td>
                <td>{r.name}</td>
                <td style={{ textAlign: "center" }}>{String(r.qty).padStart(2, "0")}</td>
                <td style={{ textAlign: "right" }}>{fmt(r.rate)}</td>
                <td style={{ textAlign: "right" }}>{fmt(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table className="summary-table">
          <tbody>
            <tr className="grand-total">
              <td>Total Amount</td>
              <td>{fmt(quotation.totalAmount)}</td>
            </tr>
          </tbody>
        </table>

        <div className="print-footer">
          <div className="print-footer-top">
            <div className="print-footer-dots">
              <span style={{ background: "#f2c94c" }} />
              <span style={{ background: "#e29b9b" }} />
              <span style={{ background: "#8fbf8f" }} />
              <span style={{ background: "#8fb8d9" }} />
            </div>
            <div className="print-footer-strapline">{CLINIC_INFO.strapline}</div>
          </div>
          <hr className="print-footer-rule" />
          <div className="print-footer-bottom">
            <div className="print-footer-left">
              <div className="clinic-name-footer">{CLINIC_INFO.clinicName}</div>
              <div>{CLINIC_INFO.tagline}</div>
              <div>{CLINIC_INFO.addressLines.join(", ")}</div>
            </div>
            <div className="print-footer-right">
              <div>{CLINIC_INFO.website}</div>
              <div>{CLINIC_INFO.phones.join(" | ")}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationPrint;
